import type {
  AggregationFieldResource,
  OCRScopeWorkResource,
} from '@/features/jobs/api/resources/ocr-scope-work'
import type { QuestionResource } from '@/features/jobs/api/resources/questions'
import type { OCRManualInputAnswer } from '@/features/jobs/scoping/ocr-manual-input'
import type { OCRScopeWorkProps } from '@/features/jobs/scoping/OCRScopeWork'
import type { OCRScopeWorkAggregation } from '@/features/jobs/scoping/ocr-scope-work-aggregation'
import type {
  OCRScopeWorkEngineStatus,
  OCRScopeWorkJobStatus,
  OCRScopeWorkStatus,
} from '@/features/jobs/scoping/ocr-scope-work-status'
import { Id } from '@/infra/id'
import { maybeParseDate, unixEpoch } from '@/utils/date-util'

export function mapOCRScopeWorkResourceToProps(
  resource: OCRScopeWorkResource,
): OCRScopeWorkProps {
  return {
    archived: resource.archived,
    confidenceLevel: resource.confidence,
    createdByName: resource.created_by_name,
    dateCreated: maybeParseDate(resource.date_created) ?? unixEpoch(),
    fileName: resource.file_name,
    id: Id(resource.id),
    jobId: Id(resource.job_id),
    jobName: resource.job_name,
    legendId: Id(resource.legend_id),
    legendVersion: resource.legend_version,
    status: mapStatusNameResourceToStatus(resource.status_name),
    engineStatus: mapStatusResourceToEngineStatus(resource.status),
    jobStatus: mapJobStatusResourceToJobStatus(resource.job_status),
    teamId: Id(resource.team_id),
    templateId: Id(resource.template_id),
    userPublicId: Id(resource.user_public_id),
    workspaceId: Id(resource.workspace_id),
  }
}

function mapStatusNameResourceToStatus(statusName: string): OCRScopeWorkStatus {
  // The status name is free text that older scope works may not match, so an
  // unexpected value must not fail the whole list.
  switch (statusName) {
    case 'Scoping in progress':
      return 'ScopingInProgress'
    case 'Error occurred':
      return 'ErrorOccurred'
    case 'Review required':
      return 'ReviewRequired'
    case 'Review suggested':
      return 'ReviewSuggested'
    case 'Ready to submit':
      return 'ReadyToSubmit'
    case 'Completed':
      return 'Completed'
    default:
      console.warn('unknown scope work status', statusName)
      return 'Unknown'
  }
}

function mapStatusResourceToEngineStatus(
  status: string,
): OCRScopeWorkEngineStatus {
  switch (status) {
    case 'COMPLETED':
      return 'Completed'
    case 'REVIEW_REQUIRED':
      return 'ReviewRequired'
    case 'REVIEW_SUGGESTED':
      return 'ReviewSuggested'
    default:
      return 'Other'
  }
}

function mapJobStatusResourceToJobStatus(
  jobStatus: string,
): OCRScopeWorkJobStatus {
  switch (jobStatus) {
    case 'READY_TO_SUBMIT':
      return 'ReadyToSubmit'
    case 'SUBMITTED':
      return 'Submitted'
    default:
      return 'Other'
  }
}

/**
 * Maps a status to the status name the search API filters by.
 */
export function mapStatusToStatusNameResource(
  status: OCRScopeWorkStatus,
): string {
  switch (status) {
    case 'ScopingInProgress':
      return 'Scoping in progress'
    case 'ErrorOccurred':
      return 'Error occurred'
    case 'ReviewRequired':
      return 'Review required'
    case 'ReviewSuggested':
      return 'Review suggested'
    case 'ReadyToSubmit':
      return 'Ready to submit'
    case 'Completed':
      return 'Completed'
    case 'Unknown':
      return ''
  }
}

export function mapAggregationFieldResourceToAggregation(
  resource: AggregationFieldResource,
): OCRScopeWorkAggregation {
  return {
    field: resource.field,
    name: resource.name,
    buckets: resource.buckets.map((b) => ({ value: b.value, count: b.count })),
  }
}

/**
 * Maps a manual input of a scope work template to its prefilled answer, which
 * is sent back as it came.
 */
export function mapManualInputResourceToAnswer(
  resource: QuestionResource,
): OCRManualInputAnswer {
  return { id: resource.id ?? '', answer: resource.answer as unknown }
}
